import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';

// Plan display names and features
const PLAN_INFO = {
  free: { name: 'Free', price: '$0/month', color: '#6b7280', bgColor: '#f3f4f6' },
  starter: { name: 'Starter', price: '$99/month', color: '#92400e', bgColor: '#fef3c7' },
  growth: { name: 'Professional', price: '$299/month', color: '#1d4ed8', bgColor: '#dbeafe' },
  enterprise: { name: 'Enterprise', price: '$799/month', color: '#5b21b6', bgColor: '#ede9fe' }
};

function Profile() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [quota, setQuota] = useState(null);
  const [planLimits, setPlanLimits] = useState(null);

  useEffect(() => {
    console.log('Profile useEffect - user object:', user);
    if (user) {
      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        company: user.company || ''
      });
      fetchQuota();
    }
  }, [user]);

  const fetchQuota = async () => {
    try {
      const response = await axios.get('/api/dashboard/quota');
      setQuota(response.data);
      setPlanLimits(response.data.limits);
    } catch (err) {
      console.error('Failed to fetch quota', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setToast(null);
    setLoading(true);

    try {
      await axios.put('/api/dashboard/profile', formData);
      setToast({ message: 'Profile updated successfully', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update profile', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setToast(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setToast({ message: 'New passwords do not match', type: 'error' });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setToast({ message: 'Password must be at least 8 characters', type: 'error' });
      return;
    }

    setLoading(true);

    try {
      await axios.put('/api/dashboard/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setToast({ message: 'Password changed successfully', type: 'success' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to change password', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  console.log('Rendering Profile - user.email:', user.email);

  const planInfo = PLAN_INFO[user.plan] || PLAN_INFO.free;

  return (
    <div className="main-content">
      <div className="content-body">
        <div className="page-header">
          <h2>Profile</h2>
          <p style={{ color: "#6b7280", fontSize: "16px" }}>Manage your personal information and password</p>
        </div>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Plan & Usage Card */}
        <div className="card">
          <div className="card-header">
            <h3>Plan & Usage</h3>
          </div>
          <div className="card-body">
            {planLimits && quota ? (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  marginBottom: '20px',
                  padding: '15px',
                  backgroundColor: planInfo.bgColor,
                  borderRadius: '8px'
                }}>
                  <div style={{
                    padding: '8px 16px',
                    backgroundColor: planInfo.color,
                    color: 'white',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    {planInfo.name}
                  </div>
                  <span style={{ color: '#374151', fontWeight: '500' }}>
                    {planInfo.price}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div style={{ padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Monitored Objects</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                      {quota.used} / {planLimits.monitoringObjects}
                    </div>
                    <div style={{
                      height: '4px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '2px',
                      marginTop: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${Math.min(100, (quota.used / planLimits.monitoringObjects) * 100)}%`,
                        height: '100%',
                        backgroundColor: quota.used >= planLimits.monitoringObjects ? '#ef4444' : '#10b981',
                        transition: 'width 0.3s'
                      }} />
                    </div>
                  </div>

                  <div style={{ padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Business Units</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                      {planLimits.businessUnits >= 999 ? 'Unlimited' : planLimits.businessUnits}
                    </div>
                  </div>

                  <div style={{ padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Check Frequency</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                      {planLimits.minCheckIntervalMinutes >= 1440 ? 'Daily' :
                       planLimits.minCheckIntervalMinutes >= 60 ? 'Hourly' :
                       `${planLimits.minCheckIntervalMinutes} min`}
                    </div>
                  </div>

                  <div style={{ padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Data Retention</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                      {planLimits.dataRetentionDays >= 365 ? '1 Year' : `${planLimits.dataRetentionDays} Days`}
                    </div>
                  </div>
                </div>

                {user.plan === 'free' && (
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: 'linear-gradient(135deg, #7c4dff 0%, #8655ff 100%)',
                    borderRadius: '8px',
                    color: 'white'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '5px' }}>Upgrade Your Plan</div>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '10px' }}>
                      Get more monitoring objects, hourly checks, and extended data retention.
                    </div>
                    <a
                      href="mailto:sales@martechmonitoring.com?subject=Upgrade Request"
                      style={{ color: 'white', textDecoration: 'underline', fontSize: '14px' }}
                    >
                      Contact Sales to Upgrade
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: '#6b7280' }}>Loading plan information...</div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Profile Information</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleProfileSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={user.email || 'NO EMAIL FOUND'}
                  disabled
                  style={{ backgroundColor: "#f3f4f6", cursor: "not-allowed", color: "#1f2937" }}
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>Email cannot be changed</small>
              </div>

              <div className="form-group">
                <label>Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Change Password</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="8"
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>Must be at least 8 characters</small>
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
